// Category-specific persuasion copy — feeds ServiceDetail's Why It Matters /
// Cost vs Value / Why Now sections. Every category has bespoke framing so
// each of the 28+ services renders compelling sales copy automatically.

const categoryCopy = {
  growth: {
    marketContext: "Every day you're invisible in search or under-served on ads, your competitors compound. Local rankings + inbound pipelines take 60–90 days to shift — waiting a quarter to start means missing two.",
    riskOfInaction: "CAC keeps climbing across every paid channel. Meta CPMs are up 40% YoY. If organic + local aren't producing pipeline, you're paying more per lead every quarter.",
    valueBucket: "Growth Marketing",
    valueDeliverables: [
      "Founder-led strategy every sprint (not a junior account manager)",
      "Full-stack execution: SEO + local + PPC + content + reporting",
      "Weekly working sessions, not monthly status decks",
      "Pipeline attribution baked into every campaign",
    ],
    comparableCost: "$8k–$25k / mo at traditional growth agencies · $10k+ / mo for a mid-level in-house marketer with benefits",
    whyNow: "Search behavior is fragmenting fast. Every quarter you're not in the map pack, in AI citations, or in the paid stack, you're ceding ground that compounds against you.",
  },
  ai: {
    marketContext: "Enterprise AI is moving from experiment to infrastructure. Every quarter you delay a private/on-prem deployment, your data leaves your control and your compliance surface widens.",
    riskOfInaction: "Public API tools leak proprietary data. Employees paste customer info into ChatGPT daily. Every leaked prompt is a potential regulatory event and a competitive advantage handed to your rivals.",
    valueBucket: "Private AI Deployment",
    valueDeliverables: [
      "Data never leaves your infrastructure — HIPAA / SOC 2 ready",
      "Fine-tuned to your workflows + brand voice",
      "Zero per-token API costs after deployment (owned, not rented)",
      "Enterprise support with dedicated engineering escalation",
    ],
    comparableCost: "$150k–$500k+ to build in-house with a 2-engineer AI team over 6–12 months",
    whyNow: "The AI infra window is closing. Enterprises deploying now lock in the operational advantage. Late movers pay 3–5x more, take twice as long, and inherit legacy pain.",
  },
  audit: {
    marketContext: "AI risk is board-level. Insurers are pricing AI incidents. Regulators are catching up. Companies without documented AI governance are one incident away from a headline event.",
    riskOfInaction: "Every AI system you deploy without a documented risk register is legal exposure. One data-leak headline can cost more than 100x the audit fee in remediation + PR + churn.",
    valueBucket: "AI Risk & Compliance Audit",
    valueDeliverables: [
      "Board-ready risk register with quantified severity + likelihood",
      "GDPR / HIPAA / SOC 2 / EU AI Act mapping — done, not theoretical",
      "Prioritized remediation roadmap with cost estimates",
      "Insurance-grade documentation for AI liability policies",
    ],
    comparableCost: "$50k–$200k / engagement at Big-4 consultancies · 6–9 month timelines",
    whyNow: "The EU AI Act took full effect in 2026. Board committees are asking for AI governance quarterly. If you don't have documented answers, you're already behind.",
  },
  web: {
    marketContext: "Your website is your highest-leverage conversion asset — and the fastest to fall out of date. Every quarter it doesn't perform is quantifiable pipeline lost to competitors with faster, higher-converting stacks.",
    riskOfInaction: "Slow, dated, or poorly converting sites bleed pipeline daily. Google downgrades core web vitals below 90. Prospects bounce in 3 seconds if the hero doesn't land.",
    valueBucket: "Web Engineering",
    valueDeliverables: [
      "Custom-designed, custom-coded — not templates",
      "Core Web Vitals ≥95 · sub-1-second LCP",
      "Full analytics + conversion tracking wired in",
      "40% below standard mid-market build rates (no agency overhead)",
    ],
    comparableCost: "$25k–$85k+ at traditional design agencies · 3–6 month timelines",
    whyNow: "Every quarter of a stale site is measurable revenue leakage. Fixing it in 4–12 weeks pays for itself within a single sales cycle at most B2B price points.",
  },
  launch: {
    marketContext: "Starting a business the DIY way costs 3–5x more in wasted time, wrong decisions, and rebuilding foundations. Structured launch = registered, branded, online, and taking money in weeks — not quarters.",
    riskOfInaction: "Every week you're not legally structured with a brand people trust, every lead is going somewhere more polished. First impressions compound.",
    valueBucket: "Zero-to-Launch",
    valueDeliverables: [
      "Legally registered LLC + EIN + Operating Agreement",
      "Full brand system (logos, colors, typography, guidelines)",
      "Custom website live on your domain",
      "Growth engine (SEO / GBP / social / reviews) preconfigured",
    ],
    comparableCost: "$8k–$25k+ pieced together across lawyers, designers, developers, and marketers",
    whyNow: "Every day you're operating under a personal name + placeholder site is a day of forgone credibility. Structured launch pays back in the first 3 clients.",
  },
  saas: {
    marketContext: "SaaS founders burn 6–9 months and $200k+ figuring out the stack (legal, brand, marketing site, Stripe billing, MVP). Structured launch collapses that to 90 days flat.",
    riskOfInaction: "Every month you're not shipping is a month competitors are validating. First-mover in your niche compounds into distribution moats you'll never catch.",
    valueBucket: "SaaS Startup Launch",
    valueDeliverables: [
      "Delaware C-Corp + IP assignments + founder vesting",
      "Full marketing site + Stripe subscription billing",
      "Production MVP web app (Phase 3)",
      "PLG automation + Product Hunt launch (Phase 3)",
    ],
    comparableCost: "$200k–$500k+ hiring a fractional CTO + agency + attorney + marketer over 9–12 months",
    whyNow: "AI has collapsed the time-to-MVP window from 12 months to 90 days. If you're not shipping this quarter, someone else in your niche is.",
  },
  video: {
    marketContext: "Video is 80% of ad performance in 2026. Traditional production costs $3k–$15k per video and takes weeks. AI production ships 20+ videos in the same window at a fraction of the cost.",
    riskOfInaction: "Without a steady stream of scroll-stopping video, your paid ads plateau, your organic reach dies, and your competitors dominate FYPs and Reels feeds.",
    valueBucket: "AI Video Production",
    valueDeliverables: [
      "5–20+ publish-ready 9:16 videos delivered in days, not months",
      "Custom AI avatar / digital twin (Tier 2+)",
      "Multilingual lip-sync + A/B ad matrix (Tier 3)",
      "60–85% below traditional agency + production rates",
    ],
    comparableCost: "$15k–$60k+ for a comparable video vault at traditional production agencies",
    whyNow: "AI video quality passed the uncanny-valley threshold in late 2025. Every quarter you wait, the gap between AI-native brands and traditional producers widens.",
  },
  os: {
    marketContext: "Most SMBs pay $250–$800/mo across 5+ SaaS tools (Calendly, Mailchimp, ActiveCampaign, HubSpot Starter, a booking tool). TraceElements OS consolidates all of it into one platform — at 30–60% of the combined cost.",
    riskOfInaction: "Every SaaS tool you don't consolidate is a lead lost between the cracks — SMS never sent, follow-up never triggered, review never requested. Compounding revenue leakage.",
    valueBucket: "TraceElements OS",
    valueDeliverables: [
      "Unified inbox (SMS + email + IG + Messenger + GBP)",
      "CRM + booking + automations + payments in one platform",
      "AI chatbot + voice bot (Tiers 2 + 3)",
      "Whitelabel mobile app for resellers (Tier 3)",
    ],
    comparableCost: "$250–$800 / mo scattered across 5+ SaaS tools · $8k–$30k / yr in effective total spend",
    whyNow: "Consolidation is the highest-ROI operations move an SMB can make in 2026. Every month you're paying 5 vendors, you're bleeding margin and coordination overhead.",
  },
  geo: {
    marketContext: "AI answered 700M+ queries yesterday. Every one that mentioned your category and NOT your brand is a lead your CAC will never touch — because that customer never even saw a search results page.",
    riskOfInaction: "Category leaders are already engineering their citations. Every quarter you're absent from ChatGPT / Claude / Gemini / Perplexity answers is a moat competitors dig deeper.",
    valueBucket: "GEO Dominance",
    valueDeliverables: [
      "Baseline citation audit across 4 major LLMs",
      "Full JSON-LD schema + entity vectoring",
      "Citation-optimized authority content + digital PR",
      "90-day AI Visibility Scorecard (evidence-based reporting)",
    ],
    comparableCost: "$25k–$100k+ / quarter split across PR firms + SEO agencies with no AI-specific coverage",
    whyNow: "AI is the new distribution layer — and the citation window is closing fast. First-mover brands in each category will own AI recommendation share for years.",
  },
};

/**
 * Build persuasion sections for a service using per-category defaults +
 * per-service overrides. Every service renders full content without needing
 * manual copy per SKU — but bespoke overrides win when present.
 */
export function buildSalesSections(svc) {
  if (!svc) return null;
  const cat = categoryCopy[svc.category] || categoryCopy.growth;

  const whyItMatters = svc.whyItMatters || {
    heading: `Why ${svc.categoryLabel || "this"} matters right now.`,
    context: cat.marketContext,
    risk: cat.riskOfInaction,
  };

  const firstOutcome = (svc.outcomes && svc.outcomes[0]) || null;

  const costValue = svc.costValue || {
    heading: "Cost vs. value — do the math.",
    yourInvestment: svc.priceLabel,
    investmentNote: svc.priceNote || svc.timeline || "",
    comparable: cat.comparableCost,
    delivered: (svc.deliverables && svc.deliverables.slice(0, 4)) || cat.valueDeliverables,
    payback: firstOutcome
      ? `Typical payback trigger: ${firstOutcome.toLowerCase()}.`
      : "Structured to pay for itself within the first delivered outcome.",
  };

  const whyNow = svc.whyNow || {
    heading: "Why now — not next quarter.",
    urgency: cat.whyNow,
    timeline: svc.timeline || svc.monthly || "Immediate scope confirmation.",
  };

  const pitch = svc.pitch || {
    heading: `${svc.name} — the pitch.`,
    line1: svc.tagline || svc.subtitle,
    line2: svc.intro,
  };

  return { pitch, whyItMatters, costValue, whyNow };
}

/**
 * Build persuasion sections at the category level (used on /services/:category
 * overview pages). Aggregates the category context and pulls the price range
 * from the child tiers so the copy adapts to whatever pricing lives underneath.
 */
export function buildCategorySalesSections(categoryKey, categoryLabel, tiers = []) {
  const cat = categoryCopy[categoryKey] || categoryCopy.growth;

  const prices = tiers.map((t) => t?.priceLabel).filter(Boolean);
  const priceRange =
    prices.length > 1 ? `${prices[0]} – ${prices[prices.length - 1]}` : prices[0] || "Custom scope";
  const timelines = tiers.map((t) => t?.timeline || t?.monthly).filter(Boolean);
  const fastestTimeline = timelines[0] || "Immediate scope confirmation.";

  return {
    whyItMatters: {
      heading: `Why ${categoryLabel || "this"} matters right now.`,
      context: cat.marketContext,
      risk: cat.riskOfInaction,
    },
    costValue: {
      heading: "Cost vs. value — do the math.",
      yourInvestment: priceRange,
      investmentNote:
        tiers.length > 1
          ? `${tiers.length} tiers · pick the fit. Full scope details on each tier page.`
          : "One-tier engagement. Full scope on the tier page.",
      comparable: cat.comparableCost,
      delivered: cat.valueDeliverables,
      payback:
        "Every tier is structured so the first delivered outcome pays back the investment.",
    },
    whyNow: {
      heading: "Why now — not next quarter.",
      urgency: cat.whyNow,
      timeline: fastestTimeline,
    },
  };
}
